import { Box, type BoxProps } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { createElement, useInsertionEffect, useMemo, type ReactNode } from 'react';
import { createAppTheme } from './theme';
import { brandThemeRawColors } from './tokens';

export type BrandThemePresetID =
  | 'heyday-indigo-v1'
  | 'ocean-blue-v1'
  | 'forest-green-v1'
  | 'deep-teal-v1'
  | 'royal-violet-v1'
  | 'burgundy-v1'
  | 'terracotta-v1'
  | 'custom-v1';

export type BrandSurfaceTone = 'cool' | 'neutral' | 'warm';

export type BrandThemeSetting = {
  presetId: BrandThemePresetID;
  primaryColor?: string;
  navigationColor?: string;
  accentColor?: string;
  surfaceTone?: BrandSurfaceTone;
};

export type BrandThemePreset = {
  id: BrandThemePresetID;
  label: string;
  description: string;
  primaryColor: string;
  navigationColor: string;
  accentColor: string;
};

export type ResolvedBrandTheme = {
  surfaceTone: BrandSurfaceTone;
  pageCanvasColor: string;
  subtleSurfaceColor: string;
  neutralRaisedSurfaceColor: string;
  dividerColor: string;
  strongDividerColor: string;
  accentColor: string;
  accentActionColor: string;
  accentStrongColor: string;
  accentTextColor: string;
  accentSoftColor: string;
  accentContrastTextColor: string;
  presetId: BrandThemePresetID;
  primaryColor: string;
  navigationColor: string;
  actionColor: string;
  actionContrastTextColor: string;
  actionDarkColor: string;
  actionSoftColor: string;
  actionBorderColor: string;
  actionSkyColor: string;
  hoverSurfaceColor: string;
  selectedHoverSurfaceColor: string;
  selectedNavigationSurfaceColor: string;
  compactSelectedSurfaceColor: string;
  navigationSurfaceColor: string;
  navigationDividerColor: string;
  navigationTextColor: string;
  navigationMutedTextColor: string;
  navigationDisabledTextColor: string;
  navigationHoverSurfaceColor: string;
  navigationHoverTextColor: string;
  navigationSelectedSurfaceColor: string;
  navigationSelectedHoverSurfaceColor: string;
  navigationSelectedTextColor: string;
  navigationSelectedHoverTextColor: string;
  navigationSelectedIconColor: string;
  navigationSelectedHoverIconColor: string;
  navigationSelectedBadgeSurfaceColor: string;
  navigationSelectedBadgeTextColor: string;
  navigationBadgeSurfaceColor: string;
  navigationBadgeTextColor: string;
  navigationPrimaryModeSurfaceColor: string;
  navigationPrimaryModeTextColor: string;
  navigationPrimaryModeHoverSurfaceColor: string;
  navigationPrimaryModeHoverTextColor: string;
  navigationSecondaryModeSurfaceColor: string;
  navigationSecondaryModeTextColor: string;
  navigationSecondaryModeHoverSurfaceColor: string;
  navigationSecondaryModeHoverTextColor: string;
  navigationFocusColor: string;
  railSurfaceColor: string;
  railTextColor: string;
  railMutedTextColor: string;
  railDividerColor: string;
  railFocusColor: string;
  railSelectedSurfaceColor: string;
  brandShadowColor: string;
  focusColor: string;
  focusStrongColor: string;
};

export const brandThemePresets: readonly BrandThemePreset[] = [
  {
    id: 'heyday-indigo-v1',
    label: '기본 인디고',
    description: '밝은 탐색 영역과 인디고 강조색',
    primaryColor: brandThemeRawColors.preset.heydayIndigo.primary,
    navigationColor: brandThemeRawColors.preset.heydayIndigo.navigation,
    accentColor: brandThemeRawColors.preset.heydayIndigo.accent,
  },
  {
    id: 'ocean-blue-v1',
    label: '오션 블루',
    description: '차분한 네이비 탐색 영역과 블루 강조색',
    primaryColor: brandThemeRawColors.preset.oceanBlue.primary,
    navigationColor: brandThemeRawColors.preset.oceanBlue.navigation,
    accentColor: brandThemeRawColors.preset.oceanBlue.accent,
  },
  {
    id: 'forest-green-v1',
    label: '포레스트 그린',
    description: '깊은 숲을 닮은 안정적인 그린 조합',
    primaryColor: brandThemeRawColors.preset.forestGreen.primary,
    navigationColor: brandThemeRawColors.preset.forestGreen.navigation,
    accentColor: brandThemeRawColors.preset.forestGreen.accent,
  },
  {
    id: 'deep-teal-v1',
    label: '딥 틸',
    description: '선명하면서도 차분한 틸 조합',
    primaryColor: brandThemeRawColors.preset.deepTeal.primary,
    navigationColor: brandThemeRawColors.preset.deepTeal.navigation,
    accentColor: brandThemeRawColors.preset.deepTeal.accent,
  },
  {
    id: 'royal-violet-v1',
    label: '로열 바이올렛',
    description: '깊이감 있는 바이올렛 조합',
    primaryColor: brandThemeRawColors.preset.royalViolet.primary,
    navigationColor: brandThemeRawColors.preset.royalViolet.navigation,
    accentColor: brandThemeRawColors.preset.royalViolet.accent,
  },
  {
    id: 'burgundy-v1',
    label: '버건디',
    description: '절제된 버건디와 와인색 탐색 영역',
    primaryColor: brandThemeRawColors.preset.burgundy.primary,
    navigationColor: brandThemeRawColors.preset.burgundy.navigation,
    accentColor: brandThemeRawColors.preset.burgundy.accent,
  },
  {
    id: 'terracotta-v1',
    label: '테라코타',
    description: '따뜻한 흙빛의 테라코타 조합',
    primaryColor: brandThemeRawColors.preset.terracotta.primary,
    navigationColor: brandThemeRawColors.preset.terracotta.navigation,
    accentColor: brandThemeRawColors.preset.terracotta.accent,
  },
  {
    id: 'custom-v1',
    label: '직접 설정',
    description: '주요색·보조색과 탐색 영역 색상을 직접 지정',
    primaryColor: brandThemeRawColors.preset.custom.primary,
    navigationColor: brandThemeRawColors.preset.custom.navigation,
    accentColor: brandThemeRawColors.preset.custom.accent,
  },
] as const;

export const defaultBrandTheme: BrandThemeSetting = {
  presetId: 'heyday-indigo-v1',
};

const presetByID = new Map<BrandThemePresetID, BrandThemePreset>(
  brandThemePresets.map((preset) => [preset.id, preset]),
);

function isBrandThemePresetID(value: unknown): value is BrandThemePresetID {
  return typeof value === 'string' && presetByID.has(value as BrandThemePresetID);
}

export function isValidHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value.trim());
}

export function normalizeHexColor(value: unknown): string | undefined {
  return isValidHexColor(value) ? value.trim().toUpperCase() : undefined;
}

type RGB = { red: number; green: number; blue: number };

function hexToRGB(color: string): RGB {
  const normalized = normalizeHexColor(color) ?? brandThemeRawColors.contrast.black;
  return {
    red: Number.parseInt(normalized.slice(1, 3), 16),
    green: Number.parseInt(normalized.slice(3, 5), 16),
    blue: Number.parseInt(normalized.slice(5, 7), 16),
  };
}

function byteToHex(value: number) {
  return Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, '0').toUpperCase();
}

export function mixHexColors(firstColor: string, secondColor: string, secondColorRatio: number): string {
  const first = hexToRGB(firstColor);
  const second = hexToRGB(secondColor);
  const ratio = Math.min(1, Math.max(0, secondColorRatio));
  return `#${byteToHex(first.red + ((second.red - first.red) * ratio))}${byteToHex(first.green + ((second.green - first.green) * ratio))}${byteToHex(first.blue + ((second.blue - first.blue) * ratio))}`;
}

function linearChannel(value: number) {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(color: string) {
  const { red, green, blue } = hexToRGB(color);
  return (0.2126 * linearChannel(red)) + (0.7152 * linearChannel(green)) + (0.0722 * linearChannel(blue));
}

export function contrastRatio(firstColor: string, secondColor: string): number {
  const first = relativeLuminance(firstColor);
  const second = relativeLuminance(secondColor);
  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}

export function contrastTextColor(backgroundColor: string): string {
  const light = brandThemeRawColors.contrast.light;
  const dark = brandThemeRawColors.contrast.black;
  return contrastRatio(backgroundColor, light) >= contrastRatio(backgroundColor, dark) ? light : dark;
}

function ensureContrast(foregroundColor: string, backgroundColor: string, minimumRatio: number) {
  if (contrastRatio(foregroundColor, backgroundColor) >= minimumRatio) return foregroundColor;
  const target = contrastTextColor(backgroundColor);
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mixHexColors(foregroundColor, target, step / 20);
    if (contrastRatio(candidate, backgroundColor) >= minimumRatio) return candidate;
  }
  return target;
}

function colorWithAlpha(color: string, alpha: number) {
  const normalized = normalizeHexColor(color) ?? brandThemeRawColors.contrast.black;
  return `${normalized}${byteToHex(Math.min(1, Math.max(0, alpha)) * 255)}`;
}

export function normalizeBrandThemeSetting(input: unknown): BrandThemeSetting {
  if (!input || typeof input !== 'object') return { ...defaultBrandTheme };
  const candidate = input as Partial<BrandThemeSetting>;
  if (!isBrandThemePresetID(candidate.presetId)) return { ...defaultBrandTheme };
  const surfaceTone = candidate.surfaceTone === 'neutral' || candidate.surfaceTone === 'warm' ? candidate.surfaceTone : undefined;
  const surfaceSetting = surfaceTone ? { surfaceTone } : {};
  if (candidate.presetId !== 'custom-v1') return { presetId: candidate.presetId, ...surfaceSetting };
  const primaryColor = normalizeHexColor(candidate.primaryColor);
  const navigationColor = normalizeHexColor(candidate.navigationColor);
  if (!primaryColor || !navigationColor) return { ...defaultBrandTheme };
  const accentColor = normalizeHexColor(candidate.accentColor);
  return {
    presetId: candidate.presetId,
    primaryColor,
    navigationColor,
    ...(accentColor ? { accentColor } : {}),
    ...surfaceSetting,
  };
}

function resolveSurfaceColors(surfaceTone: BrandSurfaceTone = 'cool') {
  const colors = brandThemeRawColors.surface[surfaceTone];
  return {
    surfaceTone,
    pageCanvasColor: colors.pageCanvas,
    subtleSurfaceColor: colors.subtle,
    neutralRaisedSurfaceColor: colors.raised,
    dividerColor: colors.divider,
    strongDividerColor: colors.strongDivider,
  };
}

function resolveAccentColors(accentColor: string) {
  const light = brandThemeRawColors.contrast.light;
  const accentActionColor = ensureContrast(accentColor, light, 4.5);
  const accentStrongColor = mixHexColors(accentActionColor, brandThemeRawColors.contrast.black, 0.18);
  const accentSoftColor = mixHexColors(accentColor, light, 0.92);
  return {
    accentColor,
    accentActionColor,
    accentStrongColor,
    accentSoftColor,
    accentTextColor: ensureContrast(accentStrongColor, accentSoftColor, 4.5),
    accentContrastTextColor: light,
  };
}

function resolveDefaultBrandTheme(surfaceTone?: BrandSurfaceTone): ResolvedBrandTheme {
  const primary = brandThemeRawColors.preset.heydayIndigo.primary;
  const navigation = brandThemeRawColors.preset.heydayIndigo.navigation;
  const light = brandThemeRawColors.contrast.light;
  return {
    ...resolveSurfaceColors(surfaceTone),
    ...resolveAccentColors(brandThemeRawColors.preset.heydayIndigo.accent),
    presetId: 'heyday-indigo-v1',
    primaryColor: primary,
    navigationColor: navigation,
    actionColor: primary,
    actionContrastTextColor: light,
    actionDarkColor: brandThemeRawColors.default.actionDark,
    actionSoftColor: brandThemeRawColors.default.actionSoft,
    actionBorderColor: brandThemeRawColors.default.brandBorder,
    actionSkyColor: brandThemeRawColors.default.brandSky,
    hoverSurfaceColor: brandThemeRawColors.default.actionSoft,
    selectedHoverSurfaceColor: brandThemeRawColors.default.selectedHoverSurface,
    selectedNavigationSurfaceColor: brandThemeRawColors.default.selectedNavigationSurface,
    compactSelectedSurfaceColor: brandThemeRawColors.default.compactSelectedSurface,
    navigationSurfaceColor: navigation,
    navigationDividerColor: brandThemeRawColors.default.navigationDivider,
    navigationTextColor: brandThemeRawColors.default.navigationText,
    navigationMutedTextColor: brandThemeRawColors.default.navigationMutedText,
    navigationDisabledTextColor: brandThemeRawColors.default.navigationDisabledText,
    navigationHoverSurfaceColor: brandThemeRawColors.default.actionSoft,
    navigationHoverTextColor: brandThemeRawColors.contrast.dark,
    navigationSelectedSurfaceColor: brandThemeRawColors.default.compactSelectedSurface,
    navigationSelectedHoverSurfaceColor: brandThemeRawColors.default.selectedNavigationSurface,
    navigationSelectedTextColor: brandThemeRawColors.default.actionDark,
    navigationSelectedHoverTextColor: brandThemeRawColors.default.actionDark,
    navigationSelectedIconColor: primary,
    navigationSelectedHoverIconColor: primary,
    navigationSelectedBadgeSurfaceColor: primary,
    navigationSelectedBadgeTextColor: light,
    navigationBadgeSurfaceColor: brandThemeRawColors.default.navigationBadgeSurface,
    navigationBadgeTextColor: brandThemeRawColors.default.navigationText,
    navigationPrimaryModeSurfaceColor: brandThemeRawColors.default.actionSoft,
    navigationPrimaryModeTextColor: brandThemeRawColors.default.actionDark,
    navigationPrimaryModeHoverSurfaceColor: brandThemeRawColors.default.selectedNavigationSurface,
    navigationPrimaryModeHoverTextColor: brandThemeRawColors.default.actionDark,
    navigationSecondaryModeSurfaceColor: light,
    navigationSecondaryModeTextColor: brandThemeRawColors.default.navigationText,
    navigationSecondaryModeHoverSurfaceColor: brandThemeRawColors.default.actionSoft,
    navigationSecondaryModeHoverTextColor: brandThemeRawColors.default.navigationText,
    navigationFocusColor: brandThemeRawColors.default.navigationFocus,
    railSurfaceColor: brandThemeRawColors.preset.heydayIndigo.rail,
    railTextColor: light,
    railMutedTextColor: brandThemeRawColors.default.railMuted,
    railDividerColor: brandThemeRawColors.default.railDivider,
    railFocusColor: brandThemeRawColors.default.railFocus,
    railSelectedSurfaceColor: brandThemeRawColors.default.railSelected,
    brandShadowColor: brandThemeRawColors.default.brandShadow,
    focusColor: brandThemeRawColors.default.focus,
    focusStrongColor: brandThemeRawColors.default.focusStrong,
  };
}

export function resolveBrandTheme(setting?: BrandThemeSetting | null): ResolvedBrandTheme {
  const normalized = normalizeBrandThemeSetting(setting);
  if (normalized.presetId === 'heyday-indigo-v1') return resolveDefaultBrandTheme(normalized.surfaceTone);

  const preset = presetByID.get(normalized.presetId) ?? presetByID.get(defaultBrandTheme.presetId)!;
  const primary = normalized.presetId === 'custom-v1' ? normalized.primaryColor! : preset.primaryColor;
  const navigation = normalized.presetId === 'custom-v1' ? normalized.navigationColor! : preset.navigationColor;
  const accent = normalized.presetId === 'custom-v1' ? normalized.accentColor ?? preset.accentColor : preset.accentColor;
  const light = brandThemeRawColors.contrast.light;
  const black = brandThemeRawColors.contrast.black;
  const action = ensureContrast(primary, light, 4.5);
  const actionContrast = contrastTextColor(action);
  const navigationContrast = contrastTextColor(navigation);
  const navigationUsesLightText = navigationContrast === light;
  const rail = navigationUsesLightText ? mixHexColors(navigation, black, 0.14) : navigation;
  const railContrast = contrastTextColor(rail);
  const navigationHoverSurface = mixHexColors(navigation, navigationContrast, 0.09);
  const navigationSelectedSurface = mixHexColors(navigation, navigationContrast, 0.18);
  const navigationSelectedHoverSurface = mixHexColors(navigation, navigationContrast, 0.25);
  const navigationBadgeSurface = mixHexColors(navigation, navigationContrast, 0.12);
  const navigationText = ensureContrast(mixHexColors(navigation, navigationContrast, 0.88), navigation, 4.5);
  const navigationMutedText = ensureContrast(mixHexColors(navigation, navigationContrast, 0.68), navigation, 4.5);
  const navigationDisabledText = ensureContrast(mixHexColors(navigation, navigationContrast, 0.48), navigation, 3);
  const navigationHoverText = ensureContrast(navigationContrast, navigationHoverSurface, 4.5);
  const navigationSelectedText = ensureContrast(navigationContrast, navigationSelectedSurface, 4.5);
  const navigationSelectedHoverText = ensureContrast(navigationContrast, navigationSelectedHoverSurface, 4.5);
  const navigationBadgeText = ensureContrast(mixHexColors(navigation, navigationContrast, 0.82), navigationBadgeSurface, 4.5);
  const navigationSecondaryModeText = ensureContrast(mixHexColors(navigation, navigationContrast, 0.82), navigation, 4.5);
  const navigationFocusSurfaces = [navigation, navigationSelectedSurface, navigationSelectedHoverSurface];
  const minimumFocusContrast = (foreground: string) => Math.min(...navigationFocusSurfaces.map((surface) => contrastRatio(foreground, surface)));
  const navigationFocus = minimumFocusContrast(light) >= minimumFocusContrast(black) ? light : black;

  return {
    ...resolveSurfaceColors(normalized.surfaceTone),
    ...resolveAccentColors(accent),
    presetId: normalized.presetId,
    primaryColor: primary,
    navigationColor: navigation,
    actionColor: action,
    actionContrastTextColor: actionContrast,
    actionDarkColor: mixHexColors(action, black, 0.18),
    actionSoftColor: mixHexColors(primary, light, 0.92),
    actionBorderColor: mixHexColors(primary, light, 0.7),
    actionSkyColor: mixHexColors(primary, light, 0.88),
    hoverSurfaceColor: mixHexColors(primary, light, 0.94),
    selectedHoverSurfaceColor: mixHexColors(primary, light, 0.82),
    selectedNavigationSurfaceColor: mixHexColors(primary, light, 0.86),
    compactSelectedSurfaceColor: mixHexColors(primary, light, 0.9),
    navigationSurfaceColor: navigation,
    navigationDividerColor: mixHexColors(navigation, navigationContrast, 0.16),
    navigationTextColor: navigationText,
    navigationMutedTextColor: navigationMutedText,
    navigationDisabledTextColor: navigationDisabledText,
    navigationHoverSurfaceColor: navigationHoverSurface,
    navigationHoverTextColor: navigationHoverText,
    navigationSelectedSurfaceColor: navigationSelectedSurface,
    navigationSelectedHoverSurfaceColor: navigationSelectedHoverSurface,
    navigationSelectedTextColor: navigationSelectedText,
    navigationSelectedHoverTextColor: navigationSelectedHoverText,
    navigationSelectedIconColor: navigationSelectedText,
    navigationSelectedHoverIconColor: navigationSelectedHoverText,
    navigationSelectedBadgeSurfaceColor: navigationContrast,
    navigationSelectedBadgeTextColor: ensureContrast(navigation, navigationContrast, 4.5),
    navigationBadgeSurfaceColor: navigationBadgeSurface,
    navigationBadgeTextColor: navigationBadgeText,
    navigationPrimaryModeSurfaceColor: navigationSelectedSurface,
    navigationPrimaryModeTextColor: navigationSelectedText,
    navigationPrimaryModeHoverSurfaceColor: navigationSelectedHoverSurface,
    navigationPrimaryModeHoverTextColor: navigationSelectedHoverText,
    navigationSecondaryModeSurfaceColor: navigation,
    navigationSecondaryModeTextColor: navigationSecondaryModeText,
    navigationSecondaryModeHoverSurfaceColor: navigationHoverSurface,
    navigationSecondaryModeHoverTextColor: navigationHoverText,
    navigationFocusColor: navigationFocus,
    railSurfaceColor: rail,
    railTextColor: railContrast,
    railMutedTextColor: colorWithAlpha(railContrast, 0.68),
    railDividerColor: colorWithAlpha(railContrast, 0.14),
    railFocusColor: railContrast,
    railSelectedSurfaceColor: colorWithAlpha(railContrast, 0.18),
    brandShadowColor: colorWithAlpha(action, 0.18),
    focusColor: colorWithAlpha(action, 0.24),
    focusStrongColor: action,
  };
}

const cssVariableForResolvedField = {
  '--app-page-canvas': 'pageCanvasColor',
  '--app-subtle-surface': 'subtleSurfaceColor',
  '--app-neutral-raised-surface': 'neutralRaisedSurfaceColor',
  '--app-divider': 'dividerColor',
  '--app-strong-divider': 'strongDividerColor',
  '--app-brand-accent': 'accentActionColor',
  '--app-brand-accent-strong': 'accentStrongColor',
  '--app-brand-accent-soft': 'accentSoftColor',
  '--app-brand-accent-text': 'accentTextColor',
  '--app-brand-accent-contrast': 'accentContrastTextColor',
  '--app-brand-action': 'actionColor',
  '--app-brand-action-dark': 'actionDarkColor',
  '--app-brand-action-soft': 'actionSoftColor',
  '--app-brand-border': 'actionBorderColor',
  '--app-brand-sky': 'actionSkyColor',
  '--app-brand-hover-surface': 'hoverSurfaceColor',
  '--app-brand-selected-hover-surface': 'selectedHoverSurfaceColor',
  '--app-brand-selected-navigation-surface': 'selectedNavigationSurfaceColor',
  '--app-brand-compact-selected-surface': 'compactSelectedSurfaceColor',
  '--app-brand-navigation-surface': 'navigationSurfaceColor',
  '--app-brand-navigation-divider': 'navigationDividerColor',
  '--app-brand-navigation-text': 'navigationTextColor',
  '--app-brand-navigation-muted-text': 'navigationMutedTextColor',
  '--app-brand-navigation-disabled-text': 'navigationDisabledTextColor',
  '--app-brand-navigation-hover-surface': 'navigationHoverSurfaceColor',
  '--app-brand-navigation-hover-text': 'navigationHoverTextColor',
  '--app-brand-navigation-selected-surface': 'navigationSelectedSurfaceColor',
  '--app-brand-navigation-selected-hover-surface': 'navigationSelectedHoverSurfaceColor',
  '--app-brand-navigation-selected-text': 'navigationSelectedTextColor',
  '--app-brand-navigation-selected-hover-text': 'navigationSelectedHoverTextColor',
  '--app-brand-navigation-selected-icon': 'navigationSelectedIconColor',
  '--app-brand-navigation-selected-hover-icon': 'navigationSelectedHoverIconColor',
  '--app-brand-navigation-selected-badge-surface': 'navigationSelectedBadgeSurfaceColor',
  '--app-brand-navigation-selected-badge-text': 'navigationSelectedBadgeTextColor',
  '--app-brand-navigation-badge-surface': 'navigationBadgeSurfaceColor',
  '--app-brand-navigation-badge-text': 'navigationBadgeTextColor',
  '--app-brand-navigation-primary-mode-surface': 'navigationPrimaryModeSurfaceColor',
  '--app-brand-navigation-primary-mode-text': 'navigationPrimaryModeTextColor',
  '--app-brand-navigation-primary-mode-hover-surface': 'navigationPrimaryModeHoverSurfaceColor',
  '--app-brand-navigation-primary-mode-hover-text': 'navigationPrimaryModeHoverTextColor',
  '--app-brand-navigation-secondary-mode-surface': 'navigationSecondaryModeSurfaceColor',
  '--app-brand-navigation-secondary-mode-text': 'navigationSecondaryModeTextColor',
  '--app-brand-navigation-secondary-mode-hover-surface': 'navigationSecondaryModeHoverSurfaceColor',
  '--app-brand-navigation-secondary-mode-hover-text': 'navigationSecondaryModeHoverTextColor',
  '--app-brand-navigation-focus': 'navigationFocusColor',
  '--app-brand-rail-surface': 'railSurfaceColor',
  '--app-brand-rail-text': 'railTextColor',
  '--app-brand-rail-muted': 'railMutedTextColor',
  '--app-brand-rail-divider': 'railDividerColor',
  '--app-brand-rail-focus': 'railFocusColor',
  '--app-brand-rail-selected': 'railSelectedSurfaceColor',
  '--app-brand-shadow': 'brandShadowColor',
  '--app-brand-focus': 'focusColor',
  '--app-brand-focus-strong': 'focusStrongColor',
} as const satisfies Readonly<Record<string, keyof ResolvedBrandTheme>>;

function resolvedCSSVariables(resolved: ResolvedBrandTheme) {
  return Object.entries(cssVariableForResolvedField).map(([variable, field]) => [variable, resolved[field]] as const);
}

export function BrandThemeProvider({ setting, children }: { setting?: BrandThemeSetting | null; children: ReactNode }) {
  const normalized = normalizeBrandThemeSetting(setting);
  const resolved = useMemo(
    () => resolveBrandTheme(normalized),
    [normalized.navigationColor, normalized.presetId, normalized.primaryColor, normalized.accentColor, normalized.surfaceTone],
  );
  const muiTheme = useMemo(() => createAppTheme(resolved), [resolved]);

  useInsertionEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const rootStyle = document.documentElement.style;
    const variables = resolvedCSSVariables(resolved);
    const previous = variables.map(([variable]) => ({
      variable,
      value: rootStyle.getPropertyValue(variable),
      priority: rootStyle.getPropertyPriority(variable),
    }));
    for (const [variable, value] of variables) rootStyle.setProperty(variable, value);

    return () => {
      for (const property of previous) {
        if (property.value) rootStyle.setProperty(property.variable, property.value, property.priority);
        else rootStyle.removeProperty(property.variable);
      }
    };
  }, [resolved]);

  return createElement(ThemeProvider, { theme: muiTheme }, children);
}

export type BrandThemePreviewProviderProps = Omit<BoxProps, 'children'> & {
  setting?: BrandThemeSetting | null;
  children: ReactNode;
};

/** 초안은 이 DOM 하위에만 적용하며 문서의 저장된 테마를 변경하지 않습니다. */
export function BrandThemePreviewProvider({ setting, children, style, ...props }: BrandThemePreviewProviderProps) {
  const normalized = normalizeBrandThemeSetting(setting);
  const resolved = useMemo(
    () => resolveBrandTheme(normalized),
    [normalized.navigationColor, normalized.presetId, normalized.primaryColor, normalized.accentColor, normalized.surfaceTone],
  );
  const muiTheme = useMemo(() => createAppTheme(resolved), [resolved]);
  const variables = useMemo(() => Object.fromEntries(resolvedCSSVariables(resolved)), [resolved]);

  return createElement(ThemeProvider, { theme: muiTheme },
    createElement(Box, { ...props, style: { ...variables, ...style } }, children),
  );
}
